/**
 * Terminal text measurement for the TUI grid and the CLI's tables: how many
 * character cells a string takes on screen, a string cut to fit a width with
 * an ellipsis, and a string flattened to one screen line. Exposed through
 * `AnsiColors` (`textWidth`, `fitText`, `oneLine`).
 *
 * Width rules (what terminals draw): a character with default emoji
 * presentation (or an emoji variation selector), a flag and an East Asian
 * wide or fullwidth character take two cells; combining marks, format
 * characters (a ZWJ sequence draws as its first emoji) and skin tones take
 * none; everything else takes one. ANSI SGR escapes take none.
 */

const SGR = /\x1b\[[0-9;]*m/g;
const ASCII_PRINTABLE = /^[\x20-\x7e]*$/;
/** A character drawn two cells wide by default (emoji presentation). */
const EMOJI = /^\p{Emoji_Presentation}$/u;
/** A character drawn on the one before it: a combining mark, a format character (ZWJ, variation selectors), a skin tone. */
const JOINS = /^[\p{Mn}\p{Me}\p{Cf}\u{1F3FB}-\u{1F3FF}]$/u;
const REGIONAL = /^[\u{1F1E6}-\u{1F1FF}]$/u;
const ZWJ = "\u200D";
const EMOJI_STYLE = "\uFE0F";

/** One grapheme (approximately: a base character with what joins it) and the cells it takes. */
interface Unit {
  text: string;
  width: number;
  /** A lone regional indicator, which the next one pairs with into a flag. */
  regional: boolean;
}

/** Whether code point `cp` is East Asian wide or fullwidth. */
function wide(cp: number): boolean {
  return (cp >= 0x1100 && cp <= 0x115f) ||
    (cp >= 0x2e80 && cp <= 0xa4cf && cp !== 0x303f) ||
    (cp >= 0xac00 && cp <= 0xd7a3) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe4f) ||
    (cp >= 0xff00 && cp <= 0xff60) ||
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x20000 && cp <= 0x3fffd);
}

/**
 * `text` as graphemes, approximately (no segmenter: a module-level one would be module state): marks, format
 * characters and skin tones join the character before them, a ZWJ joins the next one too, two regional indicators
 * make one flag, and an emoji variation selector makes its base two cells wide.
 */
function units(text: string): Unit[] {
  const out: Unit[] = [];
  let joinNext = false;
  for (const ch of text) {
    const last = out.at(-1);
    if (last != null && (joinNext || JOINS.test(ch) || (last.regional && REGIONAL.test(ch)))) {
      last.text += ch;
      if (ch === EMOJI_STYLE) last.width = 2;
      if (REGIONAL.test(ch)) last.regional = false;
      joinNext = ch === ZWJ;
      continue;
    }
    joinNext = false;
    const regional = REGIONAL.test(ch);
    out.push({ text: ch, width: EMOJI.test(ch) || regional || wide(ch.codePointAt(0) ?? 0) ? 2 : 1, regional });
  }
  return out;
}

/** The character cells `text` takes on a terminal (ANSI colour escapes take none). */
export function textWidth(text: string): number {
  const plain = text.includes("\x1b") ? text.replace(SGR, "") : text;
  if (ASCII_PRINTABLE.test(plain)) return plain.length;
  let n = 0;
  for (const unit of units(plain)) n += unit.width;
  return n;
}

/**
 * `text` when it fits in `width` cells; otherwise its longest prefix that fits
 * in `width - 1` cells followed by `…` (never splitting a grapheme). Plain
 * text only (no ANSI escapes).
 */
export function fitText(text: string, width: number): string {
  if (width <= 0) return "";
  if (textWidth(text) <= width) return text;
  if (ASCII_PRINTABLE.test(text)) return `${text.slice(0, width - 1)}…`;
  let out = "";
  let used = 0;
  for (const unit of units(text)) {
    if (used + unit.width > width - 1) break;
    out += unit.text;
    used += unit.width;
  }
  return `${out}…`;
}

/**
 * `text` as one screen line: each line break becomes `⏎`, a tab a space, and
 * every other control character (escape included) is dropped, so a cell or a
 * label never breaks or restyles the table it is drawn in.
 */
export function oneLine(text: string): string {
  if (ASCII_PRINTABLE.test(text)) return text;
  return text
    .replace(/\r\n|[\r\n\u2028\u2029]/g, "⏎")
    .replace(/\t/g, " ")
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, "");
}
